import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Check, Copy, Facebook, MessageCircle, PartyPopper } from "lucide-react";
import { API_URL } from "@/lib/api";
import { BRAND, buildWhatsAppUrl } from "@/lib/brand";

const API = API_URL;

// keys are stable across languages; the labels come from translations.
const STEP_DEFS = [
    { key: "objetivo", multi: true },
    { key: "pessoas", multi: false },
    { key: "frequencia", multi: false },
    { key: "cozinha", multi: true },
    { key: "dificuldade", multi: true },
];

const slide = {
    initial: { opacity: 0, x: 30 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -30 },
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
};

const isFilled = (val) => Array.isArray(val) ? val.length > 0 : !!val;
const displayValue = (val) => Array.isArray(val) ? val.join(", ") : (val || "-");

export const QuizForm = () => {
    const { t } = useTranslation();

    // Hydrate translated step content keyed by index — keys stay constant.
    const steps = useMemo(() => {
        const translated = t("quiz.steps", { returnObjects: true }) || [];
        return STEP_DEFS.map((def, i) => ({
            ...def,
            question: translated[i]?.question || "",
            options: translated[i]?.options || [],
        }));
    }, [t]);

    const totalSteps = steps.length + 1; // + contact step
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({
        nome: "",
        telefone: "",
        email: "",
        localidade: "",
        mensagem: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [copied, setCopied] = useState(false);

    const progress = Math.round(((step + 1) / totalSteps) * 100);

    const toggleOption = (stepDef, value) => {
        const { key, multi } = stepDef;
        setAnswers((prev) => {
            if (multi) {
                const current = Array.isArray(prev[key]) ? prev[key] : [];
                const exists = current.includes(value);
                const nextVal = exists ? current.filter((v) => v !== value) : [...current, value];
                return { ...prev, [key]: nextVal };
            }
            return { ...prev, [key]: value };
        });
        if (!stepDef.multi) {
            setTimeout(() => setStep((s) => Math.min(s + 1, totalSteps - 1)), 250);
        }
    };

    const isSelected = (stepDef, value) => {
        const v = answers[stepDef.key];
        if (stepDef.multi) return Array.isArray(v) && v.includes(value);
        return v === value;
    };

    const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
    const prev = () => setStep((s) => Math.max(s - 1, 0));

    const validateContact = () => {
        if (!contact.nome.trim() || contact.nome.trim().length < 2) return t("quiz.err_nome");
        if (!contact.telefone.trim() || contact.telefone.trim().length < 6) return t("quiz.err_telefone");
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) return t("quiz.err_email");
        if (!contact.localidade.trim()) return t("quiz.err_localidade");
        return null;
    };

    const serializeAnswers = () => {
        const out = {};
        for (const s of steps) {
            const v = answers[s.key];
            if (Array.isArray(v)) out[s.key] = v.join(", ");
            else out[s.key] = v || "";
        }
        return out;
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        const err = validateContact();
        if (err) {
            toast.error(err);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                ...contact,
                quiz: serializeAnswers(),
            });
            setDone(true);
            toast.success(t("quiz.ok"));
        } catch (err) {
            // Keep a single, safe log line: avoid leaking the raw error object/stack to users.
            if (process.env.NODE_ENV !== "production") {
                console.warn("Lead submission failed:", err?.message || err);
            }
            toast.error(t("quiz.fail"));
        } finally {
            setSubmitting(false);
        }
    };

    const buildWaMessage = () => {
        const parts = [
            `Olá! Acabei de pedir uma proposta no site.`,
            `Nome: ${contact.nome}`,
            `Localidade: ${contact.localidade}`,
            `Objetivo: ${displayValue(answers.objetivo)}`,
            `Pessoas: ${displayValue(answers.pessoas)}`,
            `Frequência: ${displayValue(answers.frequencia)}`,
            `Cozinha: ${displayValue(answers.cozinha)}`,
            `Dificuldade: ${displayValue(answers.dificuldade)}`,
            contact.mensagem ? `Mensagem: ${contact.mensagem}` : null,
        ].filter(Boolean);
        return parts.join("\n");
    };

    const openWhatsApp = () => {
        window.open(buildWhatsAppUrl(buildWaMessage()), "_blank");
    };

    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareText = t("hero.subtitle");

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success(t("quiz.link_copied"));
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error(t("quiz.link_fail"));
        }
    };

    const shareWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
        window.open(url, "_blank");
    };

    const shareFacebook = () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        window.open(url, "_blank");
    };

    if (done) {
        return (
            <section id="quiz" data-testid="thankyou-section" className="py-24 md:py-32 bg-beige-50">
                <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="bg-white rounded-3xl ring-1 ring-stone-200 p-10 md:p-16 shadow-sm"
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-beige-100 mb-7">
                            <PartyPopper className="text-gold-deep" size={28} strokeWidth={1.4} />
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight leading-[1.1]">
                            {t("quiz.thanks_title_1")}{" "}
                            <em className="italic text-gold-deep font-normal">{t("quiz.thanks_title_em")}</em>.
                        </h2>
                        <p className="mt-6 text-stone-600 font-light text-lg leading-relaxed">
                            {t("quiz.thanks_subtitle")}
                        </p>

                        <div className="my-10 h-px bg-stone-200" />

                        <p className="font-serif text-2xl md:text-3xl text-stone-900 leading-tight">
                            {t("quiz.refer_title")}
                        </p>
                        <p className="mt-3 text-stone-600 text-sm md:text-base">
                            <span className="text-gold-deep">★</span>{" "}
                            {t("quiz.refer_subtitle_1")}{" "}
                            <strong className="font-medium">{t("quiz.refer_subtitle_strong")}</strong>{" "}
                            {t("quiz.refer_subtitle_2")}
                        </p>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                                onClick={shareWhatsApp}
                                className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 px-6 py-5 h-auto"
                                data-testid="share-whatsapp"
                            >
                                <MessageCircle size={18} className="mr-2" />
                                {t("quiz.share_wa")}
                            </Button>
                            <Button
                                onClick={shareFacebook}
                                variant="outline"
                                className="rounded-full border-2 border-stone-200 hover:border-stone-900 px-6 py-5 h-auto"
                                data-testid="share-facebook"
                            >
                                <Facebook size={18} className="mr-2" />
                                {t("quiz.share_fb")}
                            </Button>
                            <Button
                                onClick={copyLink}
                                variant="outline"
                                className="rounded-full border-2 border-stone-200 hover:border-stone-900 px-6 py-5 h-auto"
                                data-testid="share-copy"
                            >
                                {copied ? <Check size={18} className="mr-2" /> : <Copy size={18} className="mr-2" />}
                                {copied ? t("quiz.share_copied") : t("quiz.share_copy")}
                            </Button>
                        </div>

                        <div className="mt-10">
                            <button
                                onClick={openWhatsApp}
                                className="text-sm text-stone-500 underline underline-offset-4 hover:text-stone-900"
                                data-testid="open-whatsapp-prefilled"
                            >
                                {t("quiz.wa_direct")}
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>
        );
    }

    const isQuiz = step < steps.length;
    const current = steps[step];
    const currentFilled = current ? isFilled(answers[current.key]) : false;

    return (
        <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32 bg-beige-50">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
                <div className="text-center mb-10 md:mb-14">
                    <div className="flex items-center justify-center gap-3 mb-5">
                        <span className="gold-divider" />
                        <span className="text-xs uppercase tracking-[0.24em] text-stone-500">
                            {t("quiz.eyebrow")}
                        </span>
                        <span className="gold-divider" />
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl text-stone-900 tracking-tight leading-[1.05]">
                        {t("quiz.title_1")}{" "}
                        <em className="italic text-gold-deep font-normal">{t("quiz.title_em")}</em>.
                    </h2>
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-3xl ring-1 ring-stone-200 shadow-sm p-8 md:p-12">
                    <div className="mb-8 md:mb-10">
                        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">
                            <span data-testid="quiz-step-indicator">
                                {t("quiz.step_indicator", { current: step + 1, total: totalSteps })}
                            </span>
                            <span>{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-1 bg-stone-200" />
                    </div>

                    <AnimatePresence mode="wait">
                        {isQuiz ? (
                            <motion.div key={`q-${step}`} {...slide}>
                                <h3 className="font-serif text-2xl md:text-3xl text-stone-900 leading-tight mb-2">
                                    {current.question}
                                </h3>
                                {current.multi && (
                                    <p className="text-xs text-stone-500 mb-6 uppercase tracking-[0.2em]">
                                        {t("quiz.multi_hint")}
                                    </p>
                                )}
                                {!current.multi && <div className="mb-7" />}

                                <div className="grid sm:grid-cols-2 gap-3">
                                    {current.options.map((opt) => {
                                        const selected = isSelected(current, opt);
                                        return (
                                            <button
                                                key={opt}
                                                onClick={() => toggleOption(current, opt)}
                                                data-testid={`option-${current.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                                className={`relative text-left px-5 py-4 rounded-xl border transition-all duration-300 ${
                                                    selected
                                                        ? "border-stone-900 bg-stone-900 text-stone-50"
                                                        : "border-stone-200 bg-white hover:border-stone-900 hover:-translate-y-0.5"
                                                }`}
                                            >
                                                <span className="text-base">{opt}</span>
                                                {current.multi && selected && (
                                                    <Check size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gold-soft" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="mt-8 flex items-center justify-between">
                                    <Button
                                        variant="ghost"
                                        onClick={prev}
                                        disabled={step === 0}
                                        className="text-stone-600 disabled:opacity-40"
                                        data-testid="quiz-prev"
                                    >
                                        <ArrowLeft size={18} className="mr-2" /> {t("quiz.back")}
                                    </Button>
                                    <Button
                                        onClick={next}
                                        disabled={!currentFilled}
                                        className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 disabled:opacity-40"
                                        data-testid="quiz-next"
                                    >
                                        {t("quiz.next")} <ArrowRight size={18} className="ml-2" />
                                    </Button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.form key="contact" {...slide} onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <h3 className="font-serif text-2xl md:text-3xl text-stone-900 leading-tight">
                                        {t("quiz.contact_title")}
                                    </h3>
                                    <p className="text-stone-500 mt-2 text-sm">
                                        {t("quiz.contact_subtitle")}
                                    </p>
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="nome" className="text-stone-700 text-sm">{t("quiz.field_nome")}</Label>
                                        <Input
                                            id="nome"
                                            data-testid="contact-nome"
                                            value={contact.nome}
                                            onChange={(e) => setContact({ ...contact, nome: e.target.value })}
                                            className="mt-1.5 bg-stone-50 border-stone-200 focus-visible:ring-0 focus-visible:border-stone-900 h-12 rounded-lg"
                                            placeholder={t("quiz.field_nome_ph")}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="telefone" className="text-stone-700 text-sm">{t("quiz.field_telefone")}</Label>
                                        <Input
                                            id="telefone"
                                            data-testid="contact-telefone"
                                            value={contact.telefone}
                                            onChange={(e) => setContact({ ...contact, telefone: e.target.value })}
                                            className="mt-1.5 bg-stone-50 border-stone-200 focus-visible:ring-0 focus-visible:border-stone-900 h-12 rounded-lg"
                                            placeholder={t("quiz.field_telefone_ph")}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="email" className="text-stone-700 text-sm">{t("quiz.field_email")}</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            data-testid="contact-email"
                                            value={contact.email}
                                            onChange={(e) => setContact({ ...contact, email: e.target.value })}
                                            className="mt-1.5 bg-stone-50 border-stone-200 focus-visible:ring-0 focus-visible:border-stone-900 h-12 rounded-lg"
                                            placeholder={t("quiz.field_email_ph")}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="localidade" className="text-stone-700 text-sm">{t("quiz.field_localidade")}</Label>
                                        <Input
                                            id="localidade"
                                            data-testid="contact-localidade"
                                            value={contact.localidade}
                                            onChange={(e) => setContact({ ...contact, localidade: e.target.value })}
                                            className="mt-1.5 bg-stone-50 border-stone-200 focus-visible:ring-0 focus-visible:border-stone-900 h-12 rounded-lg"
                                            placeholder={t("quiz.field_localidade_ph")}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="mensagem" className="text-stone-700 text-sm">
                                        {t("quiz.field_mensagem")}
                                    </Label>
                                    <Textarea
                                        id="mensagem"
                                        data-testid="contact-mensagem"
                                        value={contact.mensagem}
                                        onChange={(e) => setContact({ ...contact, mensagem: e.target.value })}
                                        className="mt-1.5 bg-stone-50 border-stone-200 focus-visible:ring-0 focus-visible:border-stone-900 rounded-lg min-h-[100px]"
                                        placeholder={t("quiz.field_mensagem_ph")}
                                    />
                                </div>

                                <div className="pt-2 flex items-center justify-between gap-3">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={prev}
                                        className="text-stone-600"
                                        data-testid="contact-back"
                                    >
                                        <ArrowLeft size={18} className="mr-2" /> {t("quiz.back")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 px-7 py-6 h-auto disabled:opacity-60"
                                        data-testid="contact-submit"
                                    >
                                        {submitting ? t("quiz.submitting") : t("quiz.submit")}
                                    </Button>
                                </div>

                                <p className="text-xs text-stone-500 pt-2">
                                    {t("quiz.consent", { brand: BRAND.name })}
                                </p>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};
